import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, Phone, Mail, MapPin, MessageCircle, CheckCircle2, AlertCircle } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WILAYAH_API = 'https://www.emsifa.com/api-wilayah-indonesia/api';

const ContactSection = () => {
  const { config } = useSiteContent();
  const contactInfo = {
    address: config.address || '',
    phone: config.phone || '',
    whatsapp: config.whatsapp || '',
    email: config.email || '',
  };
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    province: '',
    city: '',
    subject: '',
    message: '',
  });
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${WILAYAH_API}/provinces.json`).then(r => setProvinces(r.data || [])).catch(() => setProvinces([]));
  }, []);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onProvinceChange = async (e) => {
    const provName = e.target.value;
    setForm({ ...form, province: provName, city: '' });
    setCities([]);
    if (!provName) return;
    const prov = provinces.find(p => p.name === provName);
    if (!prov) return;
    setLoadingCities(true);
    try {
      const r = await axios.get(`${WILAYAH_API}/regencies/${prov.id}.json`);
      setCities(r.data || []);
    } catch {
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', msg: '' });
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data && res.data.id) {
        setStatus({ type: 'success', msg: 'Terima kasih! Pesan Anda berhasil terkirim. Tim kami akan menghubungi Anda segera.' });
        setForm({ name: '', email: '', phone: '', company: '', province: '', city: '', subject: '', message: '' });
        setCities([]);
      }
    } catch (err) {
      setStatus({
        type: 'error',
        msg: err.response?.data?.detail || 'Gagal mengirim pesan. Silakan coba lagi.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-16 lg:py-24 bg-gradient-to-br from-blue-50 via-white to-blue-50/30" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-[#0a3d8f] uppercase tracking-widest mb-2">
            Hubungi Kami
          </p>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900">
            Konsultasi & Penawaran Produk
          </h2>
          <div className="section-divider"></div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Contact info card */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-[#0a3d8f] text-white rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full" />
              <div className="absolute -bottom-16 -left-8 w-40 h-40 bg-[#0a8f3d]/20 rounded-full" />
              <div className="relative">
                <h3 className="text-2xl font-extrabold mb-2 font-[Montserrat]">JILONG Indonesia</h3>
                <p className="text-blue-100 text-sm mb-8">
                  Cabang resmi JILONG Communication di Indonesia. Kami siap membantu kebutuhan jaringan optik Anda.
                </p>

                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                      <MapPin size={18} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-blue-200 mb-0.5">Alamat</div>
                      <div className="text-sm font-medium">{contactInfo.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                      <Phone size={18} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-blue-200 mb-0.5">Telepon</div>
                      <a href={`tel:${contactInfo.phone}`} className="text-sm font-medium hover:underline">{contactInfo.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0a8f3d]/30 flex items-center justify-center shrink-0">
                      <MessageCircle size={18} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-blue-200 mb-0.5">WhatsApp</div>
                      <a
                        href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-medium hover:underline"
                      >
                        {contactInfo.whatsapp}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                      <Mail size={18} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-blue-200 mb-0.5">Email</div>
                      <a href={`mailto:${contactInfo.email}`} className="text-sm font-medium hover:underline break-all">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            className="lg:col-span-3 bg-white rounded-2xl p-8 shadow-xl border border-gray-100"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nama *</label>
                <input
                  required
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={change}
                  data-testid="contact-input-name"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-[#0a3d8f] focus:ring-2 focus:ring-[#0a3d8f]/10 transition-colors"
                  placeholder="Nama Anda"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email *</label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={change}
                  data-testid="contact-input-email"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-[#0a3d8f] focus:ring-2 focus:ring-[#0a3d8f]/10 transition-colors"
                  placeholder="email@perusahaan.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nomor WhatsApp *</label>
                <input
                  required
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={change}
                  data-testid="contact-input-phone"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-[#0a3d8f] focus:ring-2 focus:ring-[#0a3d8f]/10 transition-colors"
                  placeholder="+62 ..."
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Perusahaan</label>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={change}
                  data-testid="contact-input-company"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-[#0a3d8f] focus:ring-2 focus:ring-[#0a3d8f]/10 transition-colors"
                  placeholder="Nama perusahaan"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Provinsi</label>
                <select
                  name="province"
                  value={form.province}
                  onChange={onProvinceChange}
                  data-testid="contact-input-province"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-[#0a3d8f] focus:ring-2 focus:ring-[#0a3d8f]/10 transition-colors bg-white"
                >
                  <option value="">Pilih Provinsi</option>
                  {provinces.map((p) => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Kota / Kabupaten</label>
                <select
                  name="city"
                  value={form.city}
                  onChange={change}
                  disabled={!form.province || loadingCities}
                  data-testid="contact-input-city"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-[#0a3d8f] focus:ring-2 focus:ring-[#0a3d8f]/10 transition-colors bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {!form.province ? 'Pilih Provinsi dulu' : loadingCities ? 'Memuat...' : 'Pilih Kota / Kabupaten'}
                  </option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Subjek</label>
                <select
                  name="subject"
                  value={form.subject}
                  onChange={change}
                  data-testid="contact-input-subject"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-[#0a3d8f] focus:ring-2 focus:ring-[#0a3d8f]/10 transition-colors bg-white"
                >
                  <option value="">Pilih topik konsultasi</option>
                  <option value="Fusion Splicer">Fusion Splicer</option>
                  <option value="OTDR & Instruments">OTDR & Instruments</option>
                  <option value="ODN">ODN Solution</option>
                  <option value="Fiber Cables">Fiber Cables</option>
                  <option value="After Sales">After Sales Service</option>
                  <option value="Partnership">Partnership / Dealer</option>
                  <option value="Other">Lainnya</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Pesan *</label>
                <textarea
                  required
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={change}
                  data-testid="contact-input-message"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-md focus:outline-none focus:border-[#0a3d8f] focus:ring-2 focus:ring-[#0a3d8f]/10 transition-colors resize-none"
                  placeholder="Ceritakan kebutuhan Anda..."
                />
              </div>
            </div>

            {status.msg && (
              <div
                data-testid="contact-status"
                className={`mt-5 flex items-start gap-3 p-4 rounded-md ${
                  status.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {status.type === 'success' ? <CheckCircle2 size={20} className="shrink-0 mt-0.5" /> : <AlertCircle size={20} className="shrink-0 mt-0.5" />}
                <span className="text-sm">{status.msg}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#0a3d8f] text-white px-7 py-3 rounded-md font-semibold hover:bg-[#082e6b] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Mengirim...' : (<>Kirim Pesan <Send size={16} /></>)}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
